import pinyin from 'pinyin';

export interface PinyinOptions {
  style?: 'tone' | 'tone2' | 'to3ne' | 'initials' | 'first_letter';
  heteronym?: boolean;
  segment?: boolean;
}

/**
 * Generate pinyin for Chinese text using local library
 * This saves API tokens and provides instant results
 */
export class LocalPinyinGenerator {
  /**
   * Convert Chinese text to pinyin
   * @param chineseText - The Chinese text to convert
   * @param options - Pinyin generation options
   * @returns Generated pinyin string
   */
  static generate(chineseText: string, options: PinyinOptions = {}): string {
    const startTime = performance.now();

    try {
      if (!chineseText || typeof chineseText !== 'string') {
        return '';
      }

      // Remove non-Chinese characters for processing
      const chineseOnly = chineseText.replace(/[^\u4e00-\u9fa5]/g, '');

      if (!chineseOnly) {
        return '';
      }

      // Default options for tone marks (like nǐ hǎo)
      // segment: true enables context-aware pronunciation for ambiguous characters
      const defaultOptions = {
        style: pinyin.STYLE_TONE as any,
        heteronym: false,
        segment: true, // Enable word segmentation for context-aware pinyin
        ...options
      };

      // Generate pinyin array
      const pinyinArray = pinyin(chineseOnly, defaultOptions);

      // Flatten and join the pinyin
      const result = pinyinArray
        .map((item: string[]) => item[0] || '')
        .filter(Boolean)
        .join(' ');

      const endTime = performance.now();
      console.log(`🔤 Local pinyin generated for "${chineseText}" → "${result}" in ${(endTime - startTime).toFixed(2)}ms`);

      return result;
    } catch (error) {
      console.warn('Local pinyin generation failed:', error);
      return '';
    }
  }

  /**
   * Generate pinyin with tone numbers (like ni3 hao3)
   */
  static generateWithNumbers(chineseText: string): string {
    return this.generate(chineseText, { style: 'tone2' as any });
  }

  /**
   * Generate pinyin without tones (like ni hao)
   */
  static generateWithoutTones(chineseText: string): string {
    return this.generate(chineseText, { style: 'to3ne' as any }).replace(/[0-9]/g, '');
  }

  /**
   * Get first letter initials (like n h)
   */
  static generateInitials(chineseText: string): string {
    return this.generate(chineseText, { style: 'first_letter' as any });
  }

  /**
   * Generate all possible pronunciations for characters with multiple readings (heteronyms)
   * Useful for words like 行 (xíng/háng), 长 (cháng/zhǎng), 重 (zhòng/chóng)
   * @param chineseText - Chinese text to convert
   * @returns Array of pinyin arrays, where each inner array contains all possible pronunciations for that character
   */
  static generateAllPronunciations(chineseText: string): string[][] {
    try {
      if (!chineseText || typeof chineseText !== 'string') {
        return [];
      }

      const chineseOnly = chineseText.replace(/[^\u4e00-\u9fa5]/g, '');
      if (!chineseOnly) {
        return [];
      }

      const pinyinArray = pinyin(chineseOnly, {
        style: pinyin.STYLE_TONE as any,
        heteronym: true, // Enable multiple pronunciations
        segment: true
      });

      console.log(`🔤 All pronunciations for "${chineseText}":`, pinyinArray);
      return pinyinArray;
    } catch (error) {
      console.warn('Heteronym generation failed:', error);
      return [];
    }
  }

  /**
   * Validate if a string contains Chinese characters
   */
  static hasChinese(text: string): boolean {
    return /[\u4e00-\u9fa5]/.test(text);
  }

  /**
   * Enhanced pinyin generation with fallback strategies
   * @param chineseText - Chinese text to convert
   * @param fallbackPinyin - API-provided pinyin as fallback
   * @returns Best available pinyin
   */
  static generateWithFallback(chineseText: string, fallbackPinyin?: string): string {
    const startTime = performance.now();

    // Try local generation first
    const localPinyin = this.generate(chineseText);

    if (localPinyin) {
      const endTime = performance.now();
      console.log(`✅ Using local pinyin: "${localPinyin}" (${(endTime - startTime).toFixed(2)}ms)`);
      return localPinyin;
    }

    // Fallback to API-provided pinyin
    if (fallbackPinyin) {
      console.log(`🔄 Using API fallback pinyin: "${fallbackPinyin}"`);
      return fallbackPinyin;
    }

    console.warn('⚠️ No pinyin available for:', chineseText);
    return '';
  }

  /**
   * Cache frequently used pinyin locally for even faster access
   */
  private static pinyinCache = new Map<string, string>();

  static generateCached(chineseText: string): string {
    // Check cache first
    if (this.pinyinCache.has(chineseText)) {
      console.log(`💰 Pinyin cache hit for: "${chineseText}"`);
      return this.pinyinCache.get(chineseText)!;
    }

    // Generate and cache
    const result = this.generate(chineseText);
    if (result) {
      this.pinyinCache.set(chineseText, result);

      // Limit cache size to prevent memory issues
      if (this.pinyinCache.size > 1000) {
        const firstKey = this.pinyinCache.keys().next().value;
        this.pinyinCache.delete(firstKey);
      }
    }

    return result;
  }

  /**
   * Preload common Chinese words for instant pinyin
   */
  static preloadCommonWords() {
    const commonWords = [
      '你好', '谢谢', '再见', '对不起', '没关系', '请问', '不客气',
      '美丽', '漂亮', '好看', '可爱', '聪明', '友好', '善良',
      '吃饭', '喝水', '睡觉', '工作', '学习', '玩游戏', '看书',
      '家人', '朋友', '老师', '学生', '医生', '律师', '工程师'
    ];

    console.log('🚀 Preloading pinyin for common words...');
    const startTime = performance.now();

    commonWords.forEach(word => {
      this.generateCached(word);
    });

    const endTime = performance.now();
    console.log(`✅ Preloaded ${commonWords.length} common words in ${(endTime - startTime).toFixed(2)}ms`);
  }
}

// Default export for easier importing
export default LocalPinyinGenerator;